import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowLeft, ArrowRight, ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { Container } from '@components/common/Container';
import { AnchorLink, EditorialHeader, Overline, TextLink } from '@components/common/Editorial';
import { legalLinks } from '@config/navigation';
import { legalDocumentVersion, siteConfig } from '@config/site';
import { usePageTitle } from '@hooks/usePageTitle';
const LinkedText = ({ text }) => (_jsx(_Fragment, { children: text.split(/(https:\/\/[^\s]+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/g).map((part, index) => {
        const cleanUrl = part.replace(/[.,;:]$/, '');
        const punctuation = part.slice(cleanUrl.length);
        if (cleanUrl.startsWith('https://'))
            return (_jsxs("span", { children: [_jsx("a", { href: cleanUrl, target: "_blank", rel: "noreferrer", children: cleanUrl }), punctuation] }, index));
        if (cleanUrl.includes('@'))
            return (_jsxs("span", { children: [_jsx("a", { href: `mailto:${cleanUrl}`, children: cleanUrl }), punctuation] }, index));
        return part;
    }) }));
export const LegalPage = ({ titleKey, contentKey }) => {
    const { t, i18n } = useTranslation();
    usePageTitle(titleKey);
    const sections = t(`legalPages.content.${contentKey}.sections`, {
        returnObjects: true,
        ...siteConfig,
        authorityName: siteConfig.dataProtectionAuthority.name,
        authorityUrl: siteConfig.dataProtectionAuthority.url,
        authorityAddress: siteConfig.dataProtectionAuthority.address
    });
    const sectionId = (index) => `${contentKey}-section-${index + 1}`;
    return (_jsxs(_Fragment, { children: [_jsx("div", { id: "legal-top", children: _jsx(EditorialHeader, { eyebrow: t('interior.legal.eyebrow'), title: t(titleKey), backTo: "/", backLabel: t('interior.legal.back') }) }), _jsxs(LegalLayout, { children: [_jsxs(IndexColumn, { children: [_jsxs("nav", { "aria-label": t('interior.legal.contents'), children: [_jsx(Overline, { children: t('interior.legal.contents') }), _jsx(IndexList, { children: sections.map((section, index) => (_jsx("li", { children: _jsxs(AnchorLink, { href: `#${sectionId(index)}`, children: [_jsxs("span", { "aria-hidden": "true", children: ["0", index + 1] }), section.title] }) }, sectionId(index)))) })] }), _jsxs(DocumentNav, { "aria-label": t('interior.legal.documents'), children: [_jsx(Overline, { children: t('interior.legal.documents') }), legalLinks.map((link) => (_jsxs(DocumentLink, { to: link.to, children: [t(link.labelKey), _jsx(ArrowRight, { size: 15, "aria-hidden": "true" })] }, link.to)))] })] }), _jsxs(LegalContent, { "aria-label": t(titleKey), children: [_jsx(Updated, { children: t('legalPages.lastUpdated', {
                                    date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(`${legalDocumentVersion}T12:00:00`))
                                }) }), sections.map((section, index) => (_jsxs(Clause, { id: sectionId(index), "aria-labelledby": `${sectionId(index)}-title`, children: [_jsxs("h2", { id: `${sectionId(index)}-title`, children: [_jsxs("span", { "aria-hidden": "true", children: ["0", index + 1] }), section.title] }), section.body.map((paragraph, paragraphIndex) => (_jsx("p", { children: _jsx(LinkedText, { text: paragraph }) }, paragraphIndex)))] }, sectionId(index)))), _jsxs(DocumentEnd, { children: [_jsxs(TextLink, { to: "/", children: [_jsx(ArrowLeft, { size: 17, "aria-hidden": "true" }), t('interior.legal.back')] }), _jsxs(AnchorLink, { href: "#legal-top", children: [t('interior.legal.top'), _jsx(ArrowUp, { size: 17, "aria-hidden": "true" })] })] })] })] })] }));
};
const LegalLayout = styled(Container) `
  display: grid;
  grid-template-columns: 230px minmax(0, 760px);
  justify-content: space-between;
  align-items: start;
  gap: clamp(3rem, 7vw, 7rem);
  padding-block: 3rem 5rem;
  @media (max-width: 899px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 2rem;
    padding-block: 2rem 3rem;
  }
`;
const IndexColumn = styled.aside `
  position: sticky;
  top: 8rem;
  @media (max-width: 899px) {
    position: static;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  @media (max-width: 599px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.75rem;
  }
`;
const IndexList = styled.ol `
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  li {
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  }
  a {
    display: flex;
    align-items: baseline;
    padding-block: 0.75rem;
    gap: 0.75rem;
    font-size: 0.875rem;
  }
  a span {
    font-size: 0.6875rem;
    color: ${({ theme }) => theme.colors.muted};
  }
`;
const DocumentNav = styled.nav `
  display: grid;
  margin-top: 2.5rem;
  @media (max-width: 899px) {
    margin-top: 0;
  }
`;
const DocumentLink = styled(NavLink) `
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  gap: 1rem;
  padding: 0.5rem 0 0.5rem 0.75rem;
  border-left: 2px solid transparent;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.8125rem;
  line-height: 1.6;
  &.active {
    color: ${({ theme }) => theme.colors.navy900};
    border-left-color: ${({ theme }) => theme.colors.blue600};
    background: ${({ theme }) => theme.colors.surface};
  }
  &:hover {
    color: ${({ theme }) => theme.colors.navy900};
    text-decoration: underline;
    text-underline-offset: 4px;
  }
  svg {
    flex-shrink: 0;
  }
`;
const LegalContent = styled.article `
  min-width: 0;
  max-width: 72ch;
`;
const Updated = styled.p `
  margin: 0 0 2.5rem;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
`;
const Clause = styled.section `
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  padding-top: 1.75rem;
  margin-bottom: 2.5rem;
  h2 {
    display: flex;
    align-items: baseline;
    gap: 1rem;
    margin: 0 0 1.25rem;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: clamp(1.35rem, 2.2vw, 1.75rem);
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: -0.03em;
  }
  h2 > span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
    letter-spacing: 0;
  }
  p {
    margin: 0 0 1rem;
    color: ${({ theme }) => theme.colors.ink};
    font-size: 1.0625rem;
    line-height: 1.85;
    overflow-wrap: anywhere;
  }
  a {
    color: ${({ theme }) => theme.colors.navy900};
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;
const DocumentEnd = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  padding-top: 1.5rem;
`;
